import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import LandingAtmosphere from '../components/ui/LandingAtmosphere';
import ParrotEmblem from '../components/ui/ParrotEmblem';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import { getAupUrl, getPrivacyUrl, getTermsUrl } from '../config/legal';
import { MARKETING_PLANS } from '../content/marketingSuite';
import { getApiErrorMessage } from '../utils/errorHandling';
import {
  signupApi,
  type CreateSignupIntentPayload,
  type SignupPlan,
} from '../api/signup';

const STEPS = ['Organisation', 'Admin', 'Instance', 'Contracts', 'Payment'] as const;

const COUNTRIES = [
  { code: 'ZA', label: 'South Africa' },
  { code: 'NA', label: 'Namibia' },
  { code: 'BW', label: 'Botswana' },
  { code: 'ZW', label: 'Zimbabwe' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'US', label: 'United States' },
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'OTHER', label: 'Other' },
];

export default function Signup() {
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [intentId, setIntentId] = useState<string | null>(null);
  const [subdomainCheck, setSubdomainCheck] = useState<{
    available?: boolean;
    reason?: string;
  }>({});

  const [form, setForm] = useState({
    orgLegalName: '',
    orgTradingName: '',
    country: 'ZA',
    taxNumber: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Johannesburg',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    subdomain: '',
    plan: 'professional' as SignupPlan,
    flockSizeEstimate: '',
    requestDpa: false,
    acceptTerms: false,
    acceptPrivacy: false,
    acceptAup: false,
    authorisedAttestation: false,
  });

  const selectedPlan = useMemo(
    () => MARKETING_PLANS.find((p) => p.key === form.plan) || MARKETING_PLANS[1],
    [form.plan],
  );

  useEffect(() => {
    const name = form.subdomain.trim();
    if (name.length < 2) {
      setSubdomainCheck({});
      return;
    }
    const t = window.setTimeout(() => {
      signupApi
        .checkSubdomain(name)
        .then((r) => setSubdomainCheck({ available: r.available, reason: r.reason }))
        .catch(() => setSubdomainCheck({ available: false, reason: 'Could not check' }));
    }, 350);
    return () => window.clearTimeout(t);
  }, [form.subdomain]);

  const patch = (partial: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...partial }));

  const validateStep = (): boolean => {
    if (step === 0) {
      if (!form.orgLegalName.trim() || form.country.length < 2) {
        toast.error('Enter organisation legal name and country');
        return false;
      }
    }
    if (step === 1) {
      if (!form.adminFirstName.trim() || !form.adminLastName.trim() || !form.adminEmail.trim()) {
        toast.error('Enter admin name and email');
        return false;
      }
    }
    if (step === 2) {
      if (!form.subdomain.trim() || subdomainCheck.available === false) {
        toast.error(subdomainCheck.reason || 'Choose an available subdomain');
        return false;
      }
    }
    if (step === 3) {
      if (
        !form.acceptTerms ||
        !form.acceptPrivacy ||
        !form.acceptAup ||
        !form.authorisedAttestation
      ) {
        toast.error('Accept all contracting statements to continue');
        return false;
      }
    }
    return true;
  };

  const goNext = async () => {
    if (!validateStep()) return;

    if (step === 2 && !intentId) {
      setBusy(true);
      try {
        const payload: CreateSignupIntentPayload = {
          orgLegalName: form.orgLegalName.trim(),
          orgTradingName: form.orgTradingName.trim() || undefined,
          country: form.country === 'OTHER' ? 'ZZ' : form.country,
          taxNumber: form.taxNumber.trim() || undefined,
          timezone: form.timezone,
          adminFirstName: form.adminFirstName.trim(),
          adminLastName: form.adminLastName.trim(),
          adminEmail: form.adminEmail.trim(),
          adminPhone: form.adminPhone.trim() || undefined,
          subdomain: form.subdomain.trim(),
          plan: form.plan,
          flockSizeEstimate: form.flockSizeEstimate
            ? Number(form.flockSizeEstimate)
            : undefined,
          requestDpa: form.requestDpa,
        };
        const intent = await signupApi.createIntent(payload);
        setIntentId(intent.id);
        setStep(3);
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Could not save signup'));
      } finally {
        setBusy(false);
      }
      return;
    }

    if (step === 3 && intentId) {
      setBusy(true);
      try {
        await signupApi.acceptContracts(intentId, {
          acceptTerms: form.acceptTerms,
          acceptPrivacy: form.acceptPrivacy,
          acceptAup: form.acceptAup,
          authorisedAttestation: form.authorisedAttestation,
        });
        setStep(4);
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Could not record contracts'));
      } finally {
        setBusy(false);
      }
      return;
    }

    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const finishPaymentOrQueue = async () => {
    if (!intentId) return;
    setBusy(true);
    try {
      const result = await signupApi.checkout(intentId);
      if (result.url) {
        window.location.href = result.url;
        return;
      }
      toast.success(result.message || 'Queued for provisioning');
      navigate(`/signup/status/${intentId}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not start checkout'));
    } finally {
      setBusy(false);
    }
  };

  const termsUrl = getTermsUrl();
  const privacyUrl = getPrivacyUrl();
  const aupUrl = getAupUrl();

  return (
    <div className="marketing-landing relative min-h-screen">
      <LandingAtmosphere className="fixed inset-0" />
      <header className="relative z-20 border-b border-teal-900/8 bg-white/50 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3.5 sm:px-6">
          <Link to="/login" className="flex items-center gap-2.5">
            <ParrotEmblem className="h-8 w-8" animated={!reducedMotion} />
            <span className="marketing-brand-title text-xl font-bold">BirdMan</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-[var(--bm-landing-muted)] hover:text-[var(--bm-landing-ink)]">
            Sign in instead
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
        <h1 className="marketing-display text-3xl font-semibold text-[var(--bm-landing-ink)] sm:text-4xl">
          Start your BirdMan
        </h1>
        <p className="mt-3 max-w-xl text-[var(--bm-landing-muted)]">
          Create a dedicated instance for your aviary. P0 queues your request for provisioning after
          contracts{selectedPlan ? ` and ${selectedPlan.name} payment when billing is enabled` : ''}.
        </p>

        <ol className="mt-8 flex flex-wrap gap-2">
          {STEPS.map((label, i) => (
            <li
              key={label}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                i === step
                  ? 'bg-teal-700 text-white'
                  : i < step
                    ? 'bg-teal-100 text-teal-900'
                    : 'bg-white/70 text-slate-500'
              }`}
            >
              {i + 1}. {label}
            </li>
          ))}
        </ol>

        <div className="mt-8 rounded-3xl border border-teal-900/10 bg-white/80 p-6 shadow-[0_28px_60px_-36px_rgba(15,118,110,0.45)] backdrop-blur-xl sm:p-8">
          {step === 0 && (
            <div className="space-y-4">
              <Field label="Legal organisation name *" value={form.orgLegalName} onChange={(v) => patch({ orgLegalName: v })} />
              <Field label="Trading name" value={form.orgTradingName} onChange={(v) => patch({ orgTradingName: v })} />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Country *</label>
                <select
                  className="input"
                  value={form.country}
                  onChange={(e) => patch({ country: e.target.value })}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </div>
              <Field label="VAT / tax number" value={form.taxNumber} onChange={(v) => patch({ taxNumber: v })} />
              <Field label="Timezone" value={form.timezone} onChange={(v) => patch({ timezone: v })} />
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="First name *" value={form.adminFirstName} onChange={(v) => patch({ adminFirstName: v })} />
                <Field label="Last name *" value={form.adminLastName} onChange={(v) => patch({ adminLastName: v })} />
              </div>
              <Field label="Work email *" type="email" value={form.adminEmail} onChange={(v) => patch({ adminEmail: v })} />
              <Field label="Phone" value={form.adminPhone} onChange={(v) => patch({ adminPhone: v })} />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Plan *</label>
                <div className="grid gap-3 sm:grid-cols-3">
                  {MARKETING_PLANS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => patch({ plan: p.key })}
                      className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                        form.plan === p.key
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 bg-white hover:border-teal-300'
                      }`}
                    >
                      <div className="font-semibold text-slate-900">{p.name}</div>
                      <div className="mt-1 text-xs text-slate-500">R{p.priceMonthlyZar}/mo</div>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">{selectedPlan.blurb}</p>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Desired subdomain *</label>
                <div className="flex items-center gap-2">
                  <input
                    className="input"
                    value={form.subdomain}
                    onChange={(e) => patch({ subdomain: e.target.value.toLowerCase() })}
                    placeholder="acme-aviaries"
                  />
                  <span className="shrink-0 text-sm text-slate-500">.birdmansuite.com</span>
                </div>
                {subdomainCheck.available === true && (
                  <p className="mt-1 text-xs text-emerald-700">Available</p>
                )}
                {subdomainCheck.available === false && (
                  <p className="mt-1 text-xs text-rose-600">{subdomainCheck.reason}</p>
                )}
              </div>
              <Field
                label="Approx. flock size"
                type="number"
                value={form.flockSizeEstimate}
                onChange={(v) => patch({ flockSizeEstimate: v })}
              />
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.requestDpa}
                  onChange={(e) => patch({ requestDpa: e.target.checked })}
                />
                <span>Request a Data Processing Agreement (DPA) for our records</span>
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 text-sm text-slate-700">
              <p>
                Review and accept the contracting documents. Acceptance is logged with timestamp and
                document version for your organisation.
              </p>
              <ContractCheck
                checked={form.acceptTerms}
                onChange={(v) => patch({ acceptTerms: v })}
                label="I accept the Terms of Service"
                href={termsUrl}
              />
              <ContractCheck
                checked={form.acceptPrivacy}
                onChange={(v) => patch({ acceptPrivacy: v })}
                label="I accept the Privacy Policy"
                href={privacyUrl}
              />
              <ContractCheck
                checked={form.acceptAup}
                onChange={(v) => patch({ acceptAup: v })}
                label="I accept the Acceptable Use Policy"
                href={aupUrl}
              />
              <ContractCheck
                checked={form.authorisedAttestation}
                onChange={(v) => patch({ authorisedAttestation: v })}
                label="I am authorised to contract for this organisation"
              />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 text-sm text-slate-700">
              <p>
                <strong>{form.orgLegalName}</strong> · <code>{form.subdomain}.birdmansuite.com</code> ·{' '}
                {selectedPlan.name} (R{selectedPlan.priceMonthlyZar}/mo)
              </p>
              <p className="text-[var(--bm-landing-muted)]">
                If Stripe billing is enabled on this landing instance, you will complete Checkout next.
                Otherwise your request is queued for our operators to provision manually (trial path).
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={finishPaymentOrQueue}
                className="btn btn-primary w-full justify-center disabled:opacity-50"
              >
                {busy ? 'Working…' : 'Continue to payment / queue'}
              </button>
            </div>
          )}

          {step < 4 && (
            <div className="mt-8 flex justify-between gap-3">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy || step === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={goNext}
              >
                {busy ? 'Saving…' : 'Continue'}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input
        type={type}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function ContractCheck({
  checked,
  onChange,
  label,
  href,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  href?: string;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {label}
        {href ? (
          <>
            {' '}
            (
            <a href={href} target="_blank" rel="noopener noreferrer" className="brand-link font-semibold">
              view
            </a>
            )
          </>
        ) : null}
      </span>
    </label>
  );
}
